import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { ShieldCheck, AlertTriangle, FileText, FileSpreadsheet, ChevronLeft, ChevronRight, Scale } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtMoney = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return '-'; const p = String(iso).slice(0, 10).split('-'); return p.length === 3 ? p.reverse().join('/') : iso; };
const PAGE_SIZE = 50;

const STATUS_META = {
  ok: { label: 'COINCIDE', cls: 'bg-green-100 text-green-800 border-green-300' },
  review: { label: 'A REVISAR', cls: 'bg-amber-100 text-amber-900 border-amber-300' },
  difference: { label: 'DIFERENCIA', cls: 'bg-red-100 text-red-800 border-red-300' },
};
const BUCKET_ORDER = ['sin_asiento', 'asiento_reversado', 'iva_no_discriminado', 'diferencia_importe'];

const StatusPill = ({ status, testId }) => {
  const m = STATUS_META[status] || STATUS_META.difference;
  return <span className={`rounded-full border px-3 py-1 text-xs font-bold ${m.cls}`} data-testid={testId}>{m.label}</span>;
};

export function IvaReconciliation({ range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('purchases');
  const [page, setPage] = useState(1);
  const [entry, setEntry] = useState(null);

  const params = useCallback(() => {
    const p = { page, page_size: PAGE_SIZE };
    if (range.date_from) p.date_from = range.date_from;
    if (range.date_to) p.date_to = range.date_to;
    return p;
  }, [range, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/accounting/iva-audit`, { params: params(), withCredentials: true });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar la conciliación de IVA'); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [range]);

  const buildUrl = (path) => {
    const url = new URL(`${BACKEND_URL}${path}`);
    if (range.date_from) url.searchParams.set('date_from', range.date_from);
    if (range.date_to) url.searchParams.set('date_to', range.date_to);
    return url.toString();
  };

  const openEntry = async (n) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/accounting/entries/by-number/${n}`, { withCredentials: true });
      setEntry(r.data);
    } catch { toast.error(`No se encontró el asiento #${n}`); }
  };

  const ok = data?.status === 'ok';

  return (
    <Card className={ok ? '' : 'border-amber-300'}>
      <CardContent className="pt-6 space-y-5" data-testid="iva-reconciliation-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            {ok ? <ShieldCheck className="w-6 h-6 text-green-600 mt-0.5" /> : <AlertTriangle className="w-6 h-6 text-amber-600 mt-0.5" />}
            <div>
              <h2 className="text-lg font-semibold">Conciliación de IVA · Libros fiscales vs Contabilidad</h2>
              <p className="text-sm text-muted-foreground">
                Cada comprobante del Libro IVA cruzado contra el IVA registrado en la cuenta contable. Tolerancia {fmtMoney(data?.tolerance ?? 0.01)}.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => window.open(buildUrl('/api/accounting/iva-reconciliation.pdf'), '_blank')} data-testid="recon-pdf">
              <FileText className="w-4 h-4 mr-2" />PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open(buildUrl('/api/accounting/iva-reconciliation.xlsx'), '_blank')} data-testid="recon-xlsx">
              <FileSpreadsheet className="w-4 h-4 mr-2" />Excel
            </Button>
          </div>
        </div>

        {loading ? <p className="text-center py-8 text-muted-foreground" data-testid="recon-loading">Conciliando…</p> : data && (
          <>
            <div className="grid gap-3 md:grid-cols-3" data-testid="recon-semaforo">
              {[['purchases', 'Compras'], ['sales', 'Ventas']].map(([k, label]) => (
                <div key={k} className="rounded-lg border p-3" data-testid={`recon-light-${k}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-sm">{label}</p>
                    <StatusPill status={data[k].status} testId={`recon-status-${k}`} />
                  </div>
                  <p className="text-xs text-muted-foreground font-mono mt-1">
                    {data[k].account_mapped ? `${data[k].account_code} · ${data[k].account_name}` : 'Cuenta de IVA sin mapear'}
                  </p>
                  <p className="text-xs mt-2">
                    {data[k].book_count} comprobante(s) · {data[k].reconciled.count} conciliado(s) ·{' '}
                    <span className={data[k].exceptions.total ? 'text-amber-700 font-semibold' : ''}>{data[k].exceptions.total} excepción(es)</span>
                  </p>
                </div>
              ))}
              <div className="rounded-lg border p-3 flex flex-col justify-between" data-testid="recon-light-global">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-sm flex items-center gap-2"><Scale className="w-4 h-4" />Estado general</p>
                  <StatusPill status={data.status} testId="recon-status-global" />
                </div>
                <div className="mt-2">
                  <p className="text-xs text-muted-foreground">Diferencia total libro vs contabilidad</p>
                  <p className="text-xl font-bold font-mono" data-testid="recon-total-difference">{fmtMoney(data.total_difference)}</p>
                </div>
              </div>
            </div>

            <Tabs value={tab} onValueChange={(v) => { setTab(v); setPage(1); }}>
              <TabsList data-testid="recon-tabs">
                <TabsTrigger value="purchases" data-testid="recon-tab-purchases">Compras ({data.purchases.exceptions.total})</TabsTrigger>
                <TabsTrigger value="sales" data-testid="recon-tab-sales">Ventas ({data.sales.exceptions.total})</TabsTrigger>
              </TabsList>
              {[['purchases', 'Compras'], ['sales', 'Ventas']].map(([k, label]) => (
                <TabsContent key={k} value={k} className="space-y-5 pt-4">
                  <ConciliationTable block={data[k]} testId={k} />
                  <ExceptionsTable block={data[k]} testId={k} label={label} page={page} setPage={setPage} onEntry={openEntry} />
                  <OrphansTable block={data[k]} testId={k} onEntry={openEntry} />
                </TabsContent>
              ))}
            </Tabs>
          </>
        )}

        <EntryDialog entry={entry} onClose={() => setEntry(null)} />
      </CardContent>
    </Card>
  );
}

function ConciliationTable({ block, testId }) {
  const bk = Object.fromEntries((block.buckets || []).map(b => [b.state, b]));
  const rows = [
    { label: 'IVA según el Libro IVA', count: block.book_count, amount: block.book_iva, bold: true },
    ...BUCKET_ORDER.map(s => ({
      label: bk[s]?.label || s, sign: '(−)', count: bk[s]?.count || 0, amount: bk[s]?.amount || 0,
    })),
    { label: 'Asientos sobre la cuenta de IVA sin comprobante en el libro', sign: '(+)', count: block.orphans.count, amount: block.orphans.amount },
    { label: 'IVA registrado en contabilidad', sign: '=', amount: block.accounting_iva, bold: true },
  ];
  return (
    <div>
      <h3 className="text-sm font-semibold uppercase text-muted-foreground mb-2">Cuadro de conciliación</h3>
      <Table data-testid={`recon-table-${testId}`}>
        <TableHeader><TableRow>
          <TableHead>Concepto</TableHead>
          <TableHead className="text-right w-28">Comprob.</TableHead>
          <TableHead className="text-right w-40">Importe IVA</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} className={r.bold ? 'font-bold bg-muted/50' : ''} data-testid={`recon-row-${testId}-${i}`}>
              <TableCell>{r.sign ? <span className="text-muted-foreground mr-1">{r.sign}</span> : null}{r.label}</TableCell>
              <TableCell className="text-right">{r.count ?? ''}</TableCell>
              <TableCell className="text-right font-mono">{fmtMoney(r.amount)}</TableCell>
            </TableRow>
          ))}
          {Math.abs(block.unexplained) > (block.tolerance || 0.01) && (
            <TableRow className="text-red-700 font-semibold" data-testid={`recon-unexplained-${testId}`}>
              <TableCell>(!) Diferencia sin explicar</TableCell><TableCell />
              <TableCell className="text-right font-mono">{fmtMoney(block.unexplained)}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {!block.account_mapped && (
        <p className="mt-2 text-xs text-red-700">
          La cuenta de IVA no está mapeada en el plan de cuentas, por eso no hay nada registrado en contabilidad.
        </p>
      )}
    </div>
  );
}

function ExceptionsTable({ block, testId, label, page, setPage, onEntry }) {
  const { total, items } = block.exceptions;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (!total) {
    return (
      <div data-testid={`recon-exceptions-${testId}`}>
        <h3 className="text-sm font-semibold uppercase text-muted-foreground mb-2">Detalle de excepciones</h3>
        <p className="text-sm text-green-700" data-testid={`recon-no-exceptions-${testId}`}>
          Sin excepciones: todos los comprobantes del Libro IVA {label} tienen su asiento contable por el importe correcto.
        </p>
      </div>
    );
  }
  return (
    <div data-testid={`recon-exceptions-${testId}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="text-sm font-semibold uppercase text-muted-foreground">Detalle de excepciones ({total})</h3>
        <div className="flex items-center gap-2 text-xs">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`recon-prev-${testId}`}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span data-testid={`recon-page-${testId}`}>Página {page} de {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid={`recon-next-${testId}`}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-24">Fecha</TableHead>
            <TableHead>Comprobante</TableHead>
            <TableHead className="w-32">CUIT</TableHead>
            <TableHead>Razón social</TableHead>
            <TableHead className="text-right w-32">IVA Libro</TableHead>
            <TableHead className="text-right w-32">IVA Contab.</TableHead>
            <TableHead className="text-right w-32">Diferencia</TableHead>
            <TableHead className="w-48">Estado</TableHead>
            <TableHead className="w-32">Asientos</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((e, i) => (
              <TableRow key={e.source_id || i} data-testid={`recon-exception-${testId}-${i}`}>
                <TableCell>{fmtDate(e.date)}</TableCell>
                <TableCell className="font-mono text-xs">{e.comprobante}</TableCell>
                <TableCell className="font-mono text-xs">{e.tax_id || '-'}</TableCell>
                <TableCell className="text-sm">{e.name || '-'}</TableCell>
                <TableCell className="text-right font-mono">{fmtMoney(e.book_iva)}</TableCell>
                <TableCell className="text-right font-mono">{fmtMoney(e.accounting_iva)}</TableCell>
                <TableCell className="text-right font-mono font-semibold text-amber-700">{fmtMoney(e.difference)}</TableCell>
                <TableCell className="text-xs">{e.state_label}</TableCell>
                <TableCell className="text-xs">
                  {(e.entries || []).length === 0 ? <span className="text-muted-foreground">—</span> : e.entries.map(n => (
                    <button key={n} type="button" onClick={() => onEntry(n)} className="text-indigo-600 hover:underline font-mono mr-1"
                      data-testid={`recon-entry-link-${n}`}>#{n}</button>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function OrphansTable({ block, testId, onEntry }) {
  const items = block.orphans?.items || [];
  if (!items.length) return null;
  return (
    <div data-testid={`recon-orphans-${testId}`}>
      <h3 className="text-sm font-semibold uppercase text-muted-foreground mb-2">
        Asientos sobre la cuenta de IVA sin comprobante en el libro ({block.orphans.count})
      </h3>
      <Table>
        <TableHeader><TableRow>
          <TableHead className="w-48">Origen</TableHead><TableHead>Concepto</TableHead>
          <TableHead className="text-right w-36">IVA Contabilidad</TableHead><TableHead className="w-32">Asientos</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {items.map((o, i) => (
            <TableRow key={i} data-testid={`recon-orphan-${testId}-${i}`}>
              <TableCell className="text-xs">{o.source_type || 'manual'}</TableCell>
              <TableCell className="text-sm">{o.description || '-'}</TableCell>
              <TableCell className="text-right font-mono">{fmtMoney(o.accounting_iva)}</TableCell>
              <TableCell className="text-xs">
                {(o.entries || []).map(n => (
                  <button key={n} type="button" onClick={() => onEntry(n)} className="text-indigo-600 hover:underline font-mono mr-1"
                    data-testid={`recon-orphan-entry-${n}`}>#{n}</button>
                ))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function EntryDialog({ entry, onClose }) {
  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="recon-entry-dialog">
        <DialogHeader>
          <DialogTitle>Asiento #{entry?.entry_number}</DialogTitle>
          <DialogDescription>{fmtDate(entry?.date)} · {entry?.description}</DialogDescription>
        </DialogHeader>
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-32">Código</TableHead><TableHead>Cuenta</TableHead>
            <TableHead className="text-right w-32">Debe</TableHead><TableHead className="text-right w-32">Haber</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(entry?.lines || []).map((ln, i) => (
              <TableRow key={i}>
                <TableCell className="font-mono text-xs">{ln.account_code}</TableCell>
                <TableCell>{ln.account_name}</TableCell>
                <TableCell className="text-right font-mono">{ln.debit > 0 ? fmtMoney(ln.debit) : ''}</TableCell>
                <TableCell className="text-right font-mono">{ln.credit > 0 ? fmtMoney(ln.credit) : ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="recon-entry-close">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
